import { IconButton, IconSearch } from '@lmig/lmds-react-icons';
import { AlphanumericInput } from '@lmig/lmds-react-alphanumeric-input';
import { Form } from '@lmig/lmds-react-form';
import { FieldGroup, Message } from '@lmig/lmds-react-field-group';
import { Select, SelectOption } from '@lmig/lmds-react-select';
import states from 'Data/states.json';
import { useState } from 'react';
import { Button } from '@lmig/lmds-react-button';
import { SearchCustomers } from './Messages';
import { Link } from '@lmig/lmds-react-link';
import SmartPopover from './SmartPopover';

interface IAdvancedSearch {
  executeSearch: (message: SearchCustomers) => void;
}
export default function AdvancedSearch({ executeSearch }: IAdvancedSearch) {
  const [firstName, setFirstName] = useState<{ value?: string }>({});
  const [lastName, setLastName] = useState<{ value?: string; hasError: boolean }>({ hasError: false });
  const [zip, setZip] = useState<{ value?: string; hasError: boolean }>({ hasError: false });
  const [state, setState] = useState<{ value?: string; hasError: boolean }>({ hasError: false });
  const [errorMessages, setErrorMessages] = useState<Message[]>([]);
  const [closed, setClosed] = useState<boolean>();
  const onEnter = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') search();
  };
  const search = () => {
    let isValid: boolean = true;
    if (zip.value && !(zip.value.length == 5 || zip.value.length == 9)) {
      setErrorMessages((errorMessages) => [
        ...errorMessages,
        {
          text: 'Zip code must be five or nine digits.',
          type: 'error',
        },
      ]);
      isValid = false;
    }
    if (lastName.value && lastName.value.length == 1) {
      setErrorMessages((errorMessages) => [
        ...errorMessages,
        {
          text: 'Last name search requires at least two characters.',
          type: 'error',
        },
      ]);
      isValid = false;
    }
    if ((!zip.value && !state.value) || !lastName.value) {
      setErrorMessages((errorMessage) => [
        ...errorMessage,
        {
          text: 'Last name and state or zip are required. First name is optional.',
          type: 'error',
        },
      ]);
      isValid = false;
    }
    if (!lastName.value || (lastName.value && lastName.value.length == 1))
      setLastName((lastName) => ({ ...lastName, hasError: true }));
    if (zip.value && !(zip.value.length == 5 || zip.value.length == 9)) setZip((zip) => ({ ...zip, hasError: true }));
    if (!zip.value && !state.value) {
      setZip({ hasError: true });
      setState({ hasError: true });
    }

    if (lastName.value && isValid) {
      let message: SearchCustomers = {
        command: 'executeSearch',
        data: {
          last_name: lastName.value,
        },
      };
      if (firstName.value) message.data.first_name = firstName.value;
      if (zip.value) message.data.zip = zip.value;
      if (state.value) message.data.state = state.value;
      executeSearch(message);
      setClosed((closed) => !closed);
    }
  };

  const resetAll = () => {
    setFirstName({ value: '' });
    setLastName({ hasError: false, value: '' });
    setState({ hasError: false, value: '' });
    setZip({ hasError: false, value: '' });
    setErrorMessages([]);
  };

  const clearErrors = () => {
    if (errorMessages.length > 0) {
      setErrorMessages([]);
      setState((state) => ({ ...state, hasError: false }));
      setZip((zip) => ({ ...zip, hasError: false }));
      setLastName((lastName) => ({ ...lastName, hasError: false }));
    }
  };

  return (
    <div id="mywork-advanced-search">
      Advanced Search
      <SmartPopover
        triggerId="advanced-search-trigger-icon"
        popoverId="advanced-search-popover"
        onKeyDown={(event) => onEnter(event)}
        trigger={
          <IconButton background="transparent" id="advanced-search-trigger-icon">
            <IconSearch color="teal" size="16" />
          </IconButton>
        }
        closed={closed}
        variant="custom"
        direction="below"
        align="start"
        onPopoverClose={() => resetAll()}
      >
        {errorMessages.length == 0 && (
          <p>
            Enter name <u>and</u> state or zip
          </p>
        )}
        <Form id="advanced-search">
          <FieldGroup id="advanced-search-field-group" messages={errorMessages}>
            <div id="advanced-search-inputs">
              <div id="first-and-last">
                <AlphanumericInput
                  id="first-name-input"
                  labelVisual="First name"
                  tabIndex={4}
                  value={firstName.value}
                  onChange={(e) => setFirstName({ value: e.currentTarget.value })}
                  onKeyDown={() => clearErrors()}
                />
                <AlphanumericInput
                  id="last-name-input"
                  tabIndex={1}
                  highlightType={lastName.hasError ? 'error' : undefined}
                  labelVisual="Last name"
                  value={lastName.value}
                  onChange={(e) => setLastName({ value: e.currentTarget.value, hasError: false })}
                  onKeyDown={() => clearErrors()}
                />
              </div>
              <div id="zip-and-state">
                <AlphanumericInput
                  id="zip-input"
                  tabIndex={2}
                  highlightType={zip.hasError ? 'error' : undefined}
                  labelVisual="Zip code"
                  value={zip.value}
                  onChange={(e) => setZip({ value: e.currentTarget.value, hasError: false })}
                  mask="zipCode"
                  onKeyDown={() => clearErrors()}
                />
                <Select
                  inputProps={{ role: 'listbox', tabIndex: 3 }}
                  highlightType={state.hasError ? 'error' : undefined}
                  isRequired
                  labelA11y="State"
                  labelVisual="State"
                  placeholderOption="State"
                  value={state.value}
                  onChange={(e) => {
                    setState({ value: e.currentTarget.value, hasError: false });
                    clearErrors();
                  }}
                >
                  {states.map((state) => (
                    <SelectOption key={state.Name} value={state.Abbreviation}>
                      {state.Name}
                    </SelectOption>
                  ))}
                </Select>
              </div>
            </div>
          </FieldGroup>
          <div id="advanced-search-bottom-row">
            <Link onClick={() => resetAll()}>Reset all</Link>
            <Button onClick={() => search()}>Search</Button>
          </div>
        </Form>
      </SmartPopover>
    </div>
  );
}
